import { ColorPickerIcon, Delete02Icon, PaintBoardIcon } from "@hugeicons/core-free-icons";
import { HugeiconsIcon } from "@hugeicons/react";
import { useNode } from "@craftjs/core";
import { Avatar, Divider, IconButton, Popover, Stack, Tooltip } from "@mui/material";
import { useCallback, useContext, useState } from "react";
import { HexAlphaColorPicker, HexColorInput } from "react-colorful";
import useEyeDropper from "use-eye-dropper";
import useResponsiveValues from "../hooks/useResponsiveValues";
import { OrganizationProvider } from '@/features/websites/components/OrganizationProvider';

interface Palette {
  primary?: {
    label: string;
    value: string;
  };
  secondary?: {
    label: string;
    value: string;
  };
}

interface ColorProps {
  [key: string]: any;
}

interface Props {
  field: string;
  onChange?: (value: string) => void;
}

const ColorSettings = ({ field, onChange }: Props) => {
  const [anchorEl, setAnchorEl] = useState<HTMLButtonElement | null>(null);
  const open = Boolean(anchorEl);
  const id = open ? 'color-popover' : undefined;
  const eyeDropper = useEyeDropper();
  const [debounce, setDebounce] = useState(setTimeout(() => { }, 500));
  const responsive = useResponsiveValues();

  const {
    props,
    actions: { setProp },
  } = useNode((node) => ({
    props: node.data.props,
  }));

  const handleOpenColorPicker = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleClose = () => {
    setAnchorEl(null);
  };

  const handleColorPickerChange = (value: string) => {
    if (debounce) {
      clearTimeout(debounce);
    }

    setDebounce(
      setTimeout(() => {
        if (onChange) {
          onChange(value);
        } else {
          setProp((props: ColorProps) => {
            props[field] = responsive.setValue(props[field], value);
          });
        }
      }, 100)
    )
  }

  const handleColorDeletion = () => {
    setProp((props: ColorProps) => {
      props[field] = undefined;
    });
  }

  const handleEyeDropperChange = useCallback(() => {
    const openPicker = async () => {
      try {
        const color = await eyeDropper.open()

        if (onChange) {
          onChange(color.sRGBHex);
        } else {
          setProp((props: ColorProps) => {
            props[field] = responsive.setValue(props[field], color.sRGBHex);
          });
        }
      } catch (error) {
        console.error(error)
      }
    }
    openPicker()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [eyeDropper, field])

  const handleInputChange = (value: string) => {
    if (onChange) {
      onChange(value);
    } else {
      setProp((props: ColorProps) => {
        props[field] = responsive.setValue(props[field], value);
      });
    }
  }

  const organization = useContext(OrganizationProvider);

  return (
    <>
      <Stack direction="row" spacing={2} alignItems="center">
        <Tooltip title="Color primario" placement="top" arrow>
          <Avatar
            variant="circular"
            sx={{
              bgcolor: organization?.primaryColor ?? '#fff',
              cursor: 'pointer'
            }}
            onClick={() => {
              setProp((props: any) => {
                props[field] = responsive.setValue(
                  props[field],
                  "{{primaryColor}}"
                );
                return props;
              });
            }}
          >
            P
          </Avatar>
        </Tooltip>
        <Tooltip title="Color secundario" placement="top" arrow>
          <Avatar
            variant="circular"
            sx={{
              bgcolor: organization?.secondaryColor ?? '#fff',
              cursor: 'pointer'
            }}
            onClick={() => {
              setProp((props: any) => {
                props[field] = responsive.setValue(
                  props[field],
                  "{{secondaryColor}}"
                );
                return props;
              });
            }}
          >S</Avatar>
        </Tooltip>
        <Divider orientation="vertical" flexItem />
        <Stack direction="row">
          <Tooltip title="Paleta" placement="top" arrow>
            <IconButton size="large" onClick={handleOpenColorPicker}>
              <HugeiconsIcon icon={PaintBoardIcon} size={22} />
            </IconButton>
          </Tooltip>
          {eyeDropper.isSupported() ? (
            <Tooltip title="Clonar" placement="top" arrow>
              <IconButton size="large" onClick={handleEyeDropperChange}>
                <HugeiconsIcon icon={ColorPickerIcon} size={22} />
              </IconButton>
            </Tooltip>
          ) : null}
          <Tooltip title="Borrar" placement="top" arrow>
            <IconButton size="large" onClick={handleColorDeletion}>
              <HugeiconsIcon icon={Delete02Icon} size={22} />
            </IconButton>
          </Tooltip>
        </Stack>
        <HexColorInput
          alpha
          prefixed
          color={responsive.getValue(props[field])}
          onChange={handleInputChange}
          style={{
            width: '150px'
          }}
        />
      </Stack>
      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        id={id}

        anchorReference="anchorEl"
        anchorOrigin={{
          vertical: 'top',
          horizontal: 'center',
        }}
        transformOrigin={{
          vertical: 'bottom',
          horizontal: 'center',
        }}
        sx={{
          '& .MuiPopover-paper': {
            backgroundColor: 'transparent',
          }
        }}
      >
        <HexAlphaColorPicker
          color={responsive.getValue(props[field])}
          onChange={handleColorPickerChange}
          style={{
            width: 250,
            height: 200,
          }}
        />
      </Popover>
    </>
  );
}

export default ColorSettings;
